/**
 * Customer Record Tab Configuration
 * 
 * Defines the tabs available in the customer record view along with their
 * permission requirements. Each tab can specify section/action permissions
 * that control visibility and access.
 */

export const CUSTOMER_TABS = {
  PROFILE: {
    index: 0,
    label: 'Profile',
    key: 'profile',
    permission: { section: 'customers', level: 'read' }
  },
  RX: {
    index: 1,
    label: 'Rx',
    key: 'rx',
    permission: { section: 'rx', level: 'read' }
  },
  INVOICE: {
    index: 2,
    label: 'Invoice',
    key: 'invoice',
    permission: { section: 'invoice', level: 'read' }
  },
  INSURANCE: {
    index: 3,
    label: 'Insurance',
    key: 'insurance',
    permission: { section: 'insurance', level: 'read' }
  },
  CREDIT_MEMOS: {
    index: 4,
    label: 'Credit Memos',
    key: 'creditmemos',
    permission: { section: 'invoice', level: 'read' }
  },
  CHART: {
    index: 5,
    label: 'Chart',
    key: 'chart',
    permission: { section: 'eyeexam', level: 'read' }
  },
  APPOINTMENTS: {
    index: 6,
    label: 'Appointments',
    key: 'appointments',
    permission: { section: 'scheduling', level: 'read' }
  },
  DOCUMENTS: {
    index: 7,
    label: 'Documents',
    key: 'documents',
    permission: { section: 'invoice', level: 'read' }
  },
  // TIMELINE: {
  //   index: 8,
  //   label: 'Timeline',
  //   key: 'timeline',
  //   permission: { section: 'customers', level: 'read' }
  // },
  COMMUNICATIONS: {
    index: 8,
    label: 'Communications',
    key: 'communications',
    permission: { section: 'customers', level: 'read' }
  }
};

/**
 * Get array of tabs ordered by index
 */
export const getOrderedTabs = () => {
  return Object.values(CUSTOMER_TABS).sort((a, b) => a.index - b.index);
};

/**
 * Get tab by key
 */
export const getTabByKey = (key) => {
  return Object.values(CUSTOMER_TABS).find(tab => tab.key === key);
};

/**
 * Get tab by index
 */
export const getTabByIndex = (index) => {
  return Object.values(CUSTOMER_TABS).find(tab => tab.index === index);
};

/**
 * Filter tabs based on user permissions
 * @param {Object} can - useCan hook result
 * @returns {Array} Array of tabs user can access
 */
export const getAccessibleTabs = (can) => {
  return getOrderedTabs().filter(tab => {
    if (!tab.permission) return true; // No permission required

    const { section, level, action } = tab.permission;
    // Prefer section-level gating; write implies read in backend policy
    if (level) {
      return can.section(section, { level });
    }
    // Fallback for legacy tabs still using action
    if (action) {
      return can.action(section, action);
    }
    return true;
  });
};

/**
 * Get accessible tab indices (for tab navigation)
 */
export const getAccessibleTabIndices = (can) => {
  return getAccessibleTabs(can).map(tab => tab.index);
};

/**
 * Map query param to tab index
 */
export const getTabIndexFromQueryParam = (tabParam, can) => {
  const tab = getTabByKey(tabParam?.toLowerCase());
  if (!tab) return null;
  
  // Check permission
  if (tab.permission) {
    const { section, level, action } = tab.permission;
    const allowed = level
      ? can.section(section, { level })
      : action
        ? can.action(section, action)
        : true;
    if (!allowed) return null; // User lacks permission
  }
  
  return tab.index;
};
